import React, { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  MoreHorizontal,
  Send,
  StickyNote,
  Undo2,
  Loader2,
  CheckCircle2,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const REFUND_STATUSES = [
  { value: 'none', label: 'No refund' },
  { value: 'requested', label: 'Refund requested' },
  { value: 'partial', label: 'Partially refunded' },
  { value: 'full', label: 'Fully refunded' },
  { value: 'rejected', label: 'Refund rejected' },
];

/**
 * Per-row admin actions popover for a payment record.
 *
 *   • Resend receipt (only for successful payments)
 *   • Edit internal notes
 *   • Set/clear refund tracking status (VIEW-ONLY — no money movement)
 *
 * All three are PATCH/POST against endpoints added in Batch A; the popover
 * refreshes the parent's payment list on any successful mutation.
 */
const PaymentRowActions = ({ payment, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState(null); // 'resend' | 'notes' | 'refund' | null

  const [notes, setNotes] = useState(payment.notes || '');
  const [refundStatus, setRefundStatus] = useState(payment.refund_status || 'none');
  const [refundNotes, setRefundNotes] = useState(payment.refund_notes || '');
  const [busy, setBusy] = useState(false);
  const [resentRecently, setResentRecently] = useState(false);

  const ref = payment.paystack_reference;
  const isSuccess = payment.status === 'success';

  const reset = () => {
    setSection(null);
    setBusy(false);
  };

  const handleResend = async () => {
    if (!isSuccess) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/payments/admin/${ref}/resend-receipt`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success(`Receipt resent to ${payment.student_email || 'payer'}`);
      setResentRecently(true);
      setTimeout(() => setResentRecently(false), 2500);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(`Resend failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveNotes = async () => {
    setBusy(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/payments/admin/${ref}/notes`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success('Notes saved');
      onRefresh && onRefresh();
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(`Could not save notes: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveRefund = async () => {
    setBusy(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/payments/admin/${ref}/refund`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            refund_status: refundStatus,
            refund_notes: refundNotes,
          }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success('Refund status saved');
      onRefresh && onRefresh();
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(`Could not save refund: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleCopyEmail = async () => {
    const email = payment.student_email || '';
    if (!email) {
      toast.error('No email on file for this payment.');
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
      } else {
        const ta = document.createElement('textarea');
        ta.value = email;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Email copied');
      setOpen(false);
    } catch (e) {
      toast.error(`Could not copy: ${e.message}`);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          data-testid={`payment-actions-${ref}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 bg-white"
        align="end"
        data-testid={`payment-actions-popover-${ref}`}
      >
        {!section && (
          <div className="space-y-1">
            <p className="text-xs text-gray-500 px-1 pb-1">
              {payment.student_name || '—'} · ${Number(payment.amount || 0).toFixed(2)}{' '}
              {payment.currency || 'USD'}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2"
              onClick={handleCopyEmail}
              disabled={!payment.student_email}
              data-testid={`payment-action-copy-email-${ref}`}
            >
              <Copy className="h-4 w-4" />
              Copy email
              {!payment.student_email && (
                <span className="ml-auto text-[10px] text-gray-400">
                  no email
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2"
              disabled={!isSuccess}
              onClick={handleResend}
              data-testid={`payment-action-resend-${ref}`}
            >
              {busy && resentRecently === false ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : resentRecently ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {resentRecently ? 'Receipt sent' : 'Resend receipt'}
              {!isSuccess && (
                <span className="ml-auto text-[10px] text-gray-400">
                  success only
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2"
              onClick={() => setSection('notes')}
              data-testid={`payment-action-notes-${ref}`}
            >
              <StickyNote className="h-4 w-4" />
              Edit notes
              {payment.notes && (
                <Badge className="ml-auto bg-amber-100 text-amber-800 border border-amber-200 text-[10px]">
                  saved
                </Badge>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2"
              disabled={!isSuccess}
              onClick={() => setSection('refund')}
              data-testid={`payment-action-refund-${ref}`}
            >
              <Undo2 className="h-4 w-4" />
              Refund tracking
              {!isSuccess && (
                <span className="ml-auto text-[10px] text-gray-400">
                  success only
                </span>
              )}
            </Button>
          </div>
        )}

        {section === 'notes' && (
          <div className="space-y-3">
            <div>
              <Label htmlFor={`notes-${ref}`} className="text-sm">
                Internal notes
              </Label>
              <Textarea
                id={`notes-${ref}`}
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Paid for May sessions, invoice cleared…"
                data-testid={`payment-notes-input-${ref}`}
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={reset}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveNotes}
                disabled={busy}
                className="flex-1"
                data-testid={`payment-notes-save-${ref}`}
              >
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Save
              </Button>
            </div>
          </div>
        )}

        {section === 'refund' && (
          <div className="space-y-3">
            <div>
              <Label htmlFor={`refund-status-${ref}`} className="text-sm">
                Refund status
              </Label>
              <select
                id={`refund-status-${ref}`}
                value={refundStatus}
                onChange={(e) => setRefundStatus(e.target.value)}
                className="w-full mt-1 border border-gray-300 rounded-md p-2 bg-white text-sm"
                data-testid={`payment-refund-status-${ref}`}
              >
                {REFUND_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor={`refund-notes-${ref}`} className="text-sm">
                Refund notes
              </Label>
              <Textarea
                id={`refund-notes-${ref}`}
                rows={3}
                value={refundNotes}
                onChange={(e) => setRefundNotes(e.target.value)}
                placeholder="Why / when / who authorised…"
                data-testid={`payment-refund-notes-${ref}`}
              />
            </div>
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              View-only tracking. Issue the actual refund from the Paystack
              dashboard — this won&apos;t trigger any money movement.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={reset}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveRefund}
                disabled={busy}
                className="flex-1"
                data-testid={`payment-refund-save-${ref}`}
              >
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default PaymentRowActions;
